/**
 * 日志级别类型
 */
import Util from './Util';

type LogLevel =
  | 'default'
  | 'info'
  | 'success'
  | 'warn'
  | 'error'
  | 'debug'
  | 'critical'
  | 'system';

/**
 * 日志预设配置
 */
interface LogPreset {
  /** 日志标签 */
  label: string;
  /** 文本颜色 */
  color?: string;
  /** 背景颜色 */
  bg?: string;
  /** 文本样式 */
  style?: string;
}

/**
 * ANSI 颜色代码
 */
const ANSI_COLORS = {
  // 文本颜色
  black: '\x1b[30m',
  red: '\x1b[31m',
  green: '\x1b[32m',
  yellow: '\x1b[33m',
  blue: '\x1b[34m',
  magenta: '\x1b[35m',
  cyan: '\x1b[36m',
  white: '\x1b[37m',
  // 背景颜色
  bgBlack: '\x1b[40m',
  bgRed: '\x1b[41m',
  bgGreen: '\x1b[42m',
  bgYellow: '\x1b[43m',
  bgBlue: '\x1b[44m',
  bgMagenta: '\x1b[45m',
  bgCyan: '\x1b[46m',
  bgWhite: '\x1b[47m',
  // 样式
  bold: '\x1b[1m',
  dim: '\x1b[2m',
  italic: '\x1b[3m',
  underscore: '\x1b[4m',
  // 重置
  reset: '\x1b[0m',
};

/**
 * 浏览器控制台颜色映射
 */
const BROWSER_COLORS: Record<string, string> = {
  black: '#000000',
  red: '#ff4444',
  green: '#44ff44',
  yellow: '#ffff44',
  blue: '#4444ff',
  magenta: '#ff44ff',
  cyan: '#44ffff',
  white: '#ffffff',
};

/**
 * 分级日志工具类
 * 提供彩色、分级的日志输出功能
 */
export default class LogUtil {
  /**
   * 预设日志类型配置
   */
  static readonly PRESETS: Record<LogLevel, LogPreset> = {
    default: { label: 'LOG', color: 'cyan' },
    info: { label: 'INFO', color: 'blue' },
    success: { label: 'SUCCESS', color: 'green', style: 'bold' },
    warn: { label: 'WARN', color: 'yellow', style: 'italic' },
    error: { label: 'ERROR', color: 'red', bg: 'bgRed', style: 'bold' },
    debug: { label: 'DEBUG', color: 'magenta', style: 'dim' },
    critical: { label: 'CRITICAL', color: 'white', bg: 'bgRed', style: 'bold' },
    system: { label: 'SYSTEM', color: 'cyan', style: 'underscore' },
  };

  /**
   * 配置选项
   */
  static config = {
    /** 是否显示时间戳 */
    showTimestamp: true,
    /** 时间格式 */
    timeFormat: 'HH:mm:ss',
    /** 运行环境 */
    environment: typeof window !== 'undefined' ? 'browser' : 'node',
  };

  /**
   * 信息级别日志 (蓝色)
   */
  static info(...messages: unknown[]): void {
    this.log('info', ...messages);
  }

  /**
   * 成功日志 (绿色加粗)
   */
  static success(...messages: unknown[]): void {
    this.log('success', ...messages);
  }

  /**
   * 警告日志 (黄色斜体)
   */
  static warn(...messages: unknown[]): void {
    this.log('warn', ...messages);
  }

  /**
   * 错误日志 (红底白字加粗)
   */
  static error(...messages: unknown[]): void {
    this.log('error', ...messages);
  }

  /**
   * 调试日志 (品红暗淡)
   */
  static debug(...messages: unknown[]): void {
    this.log('debug', ...messages);
  }

  /**
   * 严重错误日志 (白字红底加粗)
   */
  static critical(...messages: unknown[]): void {
    this.log('critical', ...messages);
  }

  /**
   * 系统级日志 (青色下划线)
   */
  static system(...messages: unknown[]): void {
    this.log('system', ...messages);
  }

  /**
   * 自定义日志
   * @param label - 自定义标签
   * @param preset - 自定义预设配置
   * @param messages - 日志消息
   */
  static custom(
    label: string,
    preset: Partial<LogPreset>,
    ...messages: unknown[]
  ): void {
    const customPreset: LogPreset = {
      label,
      color: preset.color || 'cyan',
      bg: preset.bg,
      style: preset.style,
    };
    this.logWithPreset(customPreset, ...messages);
  }

  /**
   * 核心日志方法
   * @param level - 日志级别
   * @param messages - 日志消息
   */
  static log(level: LogLevel = 'default', ...messages: unknown[]): void {
    const preset = LogUtil.PRESETS[level];
    this.logWithPreset(preset, ...messages);
  }

  /**
   * 使用预设配置输出日志
   * @private
   */
  private static logWithPreset(
    preset: LogPreset,
    ...messages: unknown[]
  ): void {
    // 统一日志开关：避免调用方漏掉 Util.isLog 判断导致生产环境刷屏
    if (!Util.isLog) return;

    const timestamp = this.getFormattedTimestamp();
    const label = `[${preset.label}]`;

    if (this.config.environment === 'node') {
      this.logNode(preset, timestamp, label, ...messages);
    } else {
      this.logBrowser(preset, timestamp, label, ...messages);
    }
  }

  /**
   * Node.js 环境日志输出
   * @private
   */
  private static logNode(
    preset: LogPreset,
    timestamp: string,
    label: string,
    ...messages: unknown[]
  ): void {
    let style = '';

    // 添加样式
    if (preset.style && ANSI_COLORS[preset.style as keyof typeof ANSI_COLORS]) {
      style += ANSI_COLORS[preset.style as keyof typeof ANSI_COLORS];
    }

    // 添加背景色
    if (preset.bg && ANSI_COLORS[preset.bg as keyof typeof ANSI_COLORS]) {
      style += ANSI_COLORS[preset.bg as keyof typeof ANSI_COLORS];
    }

    // 添加文本颜色
    if (preset.color && ANSI_COLORS[preset.color as keyof typeof ANSI_COLORS]) {
      style += ANSI_COLORS[preset.color as keyof typeof ANSI_COLORS];
    }

    const reset = ANSI_COLORS.reset;
    const prefix = this.config.showTimestamp
      ? `${style}${timestamp} ${label}${reset}`
      : `${style}${label}${reset}`;

    console.log(prefix, ...messages);
  }

  /**
   * 浏览器环境日志输出
   * @private
   */
  private static logBrowser(
    preset: LogPreset,
    timestamp: string,
    label: string,
    ...messages: unknown[]
  ): void {
    let css = '';

    // 添加文本颜色
    if (preset.color && BROWSER_COLORS[preset.color]) {
      css += `color: ${BROWSER_COLORS[preset.color]};`;
    }

    // 添加背景色
    if (preset.bg) {
      const bgColor = preset.bg.replace('bg', '').toLowerCase();
      if (BROWSER_COLORS[bgColor]) {
        css += `background-color: ${BROWSER_COLORS[bgColor]};`;
        css += 'padding: 2px 6px;';
        css += 'border-radius: 3px;';
      }
    }

    // 添加样式
    if (preset.style === 'bold') {
      css += 'font-weight: bold;';
    } else if (preset.style === 'italic') {
      css += 'font-style: italic;';
    } else if (preset.style === 'underscore') {
      css += 'text-decoration: underline;';
    } else if (preset.style === 'dim') {
      css += 'opacity: 0.6;';
    }

    const prefix = this.config.showTimestamp
      ? `%c${timestamp} ${label}`
      : `%c${label}`;

    console.log(prefix, css, ...messages);
  }

  /**
   * 获取格式化的时间戳
   * @private
   */
  private static getFormattedTimestamp(): string {
    if (!this.config.showTimestamp) return '';

    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${hours}:${minutes}:${seconds}`;
  }
}
